import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ProducerModel } from '@bo-client/activation-management/grid-registration/model/producer.model';
import { StorageModel } from '@bo-client/activation-management/grid-registration/model/storage.model';
import { DataShareService } from '@bo-client/shared/services/data-share.service';
import { AuthorizationService } from '@bo-client/core/authorization/authorization.service';
import { MetaService } from '@bo-client/core/api/meta-service';
import { GridRegistrationModel } from '@bo-client/activation-management/grid-registration/model/grid-registration.model';
import { combineLatest, Observable } from 'rxjs';
import { Observe } from '@bo-client/shared/observe.util';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { map, startWith } from 'rxjs/operators';
import { FeederContractModel } from '@bo-client/activation-management/feeder-contract/model/feeder-contract.model';

@Component({
  selector: 'app-registration-declaration-data',
  templateUrl: './registration-declaration-data.component.html',
  styleUrls: ['./registration-declaration-data.component.scss']
})
export class RegistrationDeclarationDataComponent implements OnInit {
  @Input() inputForm!: FormGroup;
  @Input() model: GridRegistrationModel | FeederContractModel = null;
  @Observe('model') model$: Observable<GridRegistrationModel | FeederContractModel>;
  @Input() producers: ProducerModel[];
  @Observe('producers') producers$: Observable<ProducerModel[]>;
  @Input() storages: StorageModel[];
  @Observe('storages') storages$: Observable<StorageModel[]>;
  @Input() contractType: string;
  @Observe('contractType') contractType$: Observable<string>;
  @Input() isInstaller = false;
  @Input() disableConditions = false;

  @Output() mastrDataMismatchFields = new EventEmitter<string[]>();

  maStRNumberProducer$: Observable<string>;

  /** Sichtbarkeit des EEG-Anlagenschlüssels der Erzeugungseinheit. */
  showEegProducerKey$: Observable<boolean>;
  /** Sichtbarkeit des EEG-Anlagenschlüssels der Speichereinheit. */
  showEegStorageKey$: Observable<boolean>;

  private readonly destroyRef = inject(DestroyRef);

  get showAdditionalMastrFields() {
    return (
      this.authorizationService.hasRole('ROLE_EDITOR_BASE') &&
      this.dataShareService.tenantConfig.feederContract.showAdditionalMastrFields &&
      !!this.contractType // this should prevent displaying this stuff if the compoennnt is embedded in a gridRegistration
    );
  }

  constructor(
    private dataShareService: DataShareService,
    public metaService: MetaService,
    private authorizationService: AuthorizationService
  ) {}

  ngOnInit() {
    this.maStRNumberProducer$ = this.inputForm.controls.maStRNumberProducer.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
      startWith(this.inputForm.controls.maStRNumberProducer.value)
    );
    this.showEegProducerKey$ = this.eegKeyVisible$('feederContract.showEegProducerKey');
    this.showEegStorageKey$ = this.eegKeyVisible$('feederContract.showEegStorageKey');
  }

  /**
   * Die EEG-Anlagenschlüssel sind nur sichtbar, wenn der Nutzer Sachbearbeiter ist
   * (`ROLE_EDITOR_FEEDERCONTRACTS`), die zugehörige Prozesskonfiguration aktiv ist und die
   * Komponente in einem Einspeisevertrag eingebettet ist — `contractType` ist in der
   * Netzanmeldung nicht gesetzt.
   */
  private eegKeyVisible$(configPath: string): Observable<boolean> {
    return combineLatest([
      this.authorizationService.hasRole$('ROLE_EDITOR_FEEDERCONTRACTS'),
      this.dataShareService.getSingleTenantConfig$<boolean>(configPath, false),
      this.contractType$
    ]).pipe(
      map(([hasEditorRole, configActive, contractType]) => hasEditorRole && configActive && !!contractType)
    );
  }
}
